import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import {
  Coupon,
  CouponCreateData,
  CouponUpdateData,
  CouponFilters,
  CouponValidationResult,
  CouponUsage,
  CouponSettings,
  CouponStats,
  createCouponId,
  createDefaultCouponSettings,
  calculateCouponDiscount,
  isCouponValid,
  getCouponStatus,
  formatCouponCode
} from '../types';

class CouponService {
  private readonly couponsCollection = 'coupons';
  private readonly couponUsageCollection = 'couponUsage';
  private readonly couponSettingsCollection = 'couponSettings';

  // Create a new coupon
  async createCoupon(couponData: CouponCreateData, createdBy: string): Promise<Coupon> {
    try {
      const couponId = createCouponId();
      const formattedCode = formatCouponCode(couponData.code);
      
      // Check if coupon code already exists
      const existingCoupon = await this.getCouponByCode(formattedCode);
      if (existingCoupon) {
        throw new Error('Coupon code already exists');
      }

      const coupon: Omit<Coupon, 'id'> = {
        code: formattedCode,
        name: couponData.name,
        description: couponData.description,
        discountType: couponData.discountType,
        discountValue: couponData.discountValue,
        minimumOrderAmount: couponData.minimumOrderAmount,
        maximumDiscountAmount: couponData.maximumDiscountAmount,
        usageLimit: couponData.usageLimit,
        usedCount: 0,
        isActive: true,
        validFrom: couponData.validFrom,
        validUntil: couponData.validUntil,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy
      };

      const docRef = await addDoc(collection(db, this.couponsCollection), {
        ...coupon,
        validFrom: Timestamp.fromDate(coupon.validFrom),
        validUntil: coupon.validUntil ? Timestamp.fromDate(coupon.validUntil) : null,
        createdAt: Timestamp.fromDate(coupon.createdAt),
        updatedAt: Timestamp.fromDate(coupon.updatedAt)
      });

      return {
        id: docRef.id,
        ...coupon
      };
    } catch (error) {
      console.error('Error creating coupon:', error);
      throw error;
    }
  }

  // Update an existing coupon
  async updateCoupon(couponId: string, updates: CouponUpdateData): Promise<Coupon> {
    try {
      const couponRef = doc(db, this.couponsCollection, couponId);
      const couponDoc = await getDoc(couponRef);
      
      if (!couponDoc.exists()) {
        throw new Error('Coupon not found');
      }

      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Convert dates to Timestamps if they exist
      if (updates.validFrom) {
        updateData.validFrom = Timestamp.fromDate(updates.validFrom);
      }
      if (updates.validUntil) {
        updateData.validUntil = Timestamp.fromDate(updates.validUntil);
      }

      await updateDoc(couponRef, updateData);

      // Return updated coupon
      const updatedDoc = await getDoc(couponRef);
      return this.mapFirestoreToCoupon(updatedDoc);
    } catch (error) {
      console.error('Error updating coupon:', error);
      throw error;
    }
  }

  // Delete a coupon
  async deleteCoupon(couponId: string): Promise<void> {
    try {
      const couponRef = doc(db, this.couponsCollection, couponId);
      await deleteDoc(couponRef);
    } catch (error) {
      console.error('Error deleting coupon:', error);
      throw error;
    }
  }

  // Get a single coupon by ID
  async getCoupon(couponId: string): Promise<Coupon | null> {
    try {
      const couponRef = doc(db, this.couponsCollection, couponId);
      const couponDoc = await getDoc(couponRef);
      
      if (!couponDoc.exists()) {
        return null;
      }

      return this.mapFirestoreToCoupon(couponDoc);
    } catch (error) {
      console.error('Error getting coupon:', error);
      throw error;
    }
  }

  // Get coupon by code
  async getCouponByCode(code: string): Promise<Coupon | null> {
    try {
      const formattedCode = formatCouponCode(code);
      const q = query(
        collection(db, this.couponsCollection),
        where('code', '==', formattedCode)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }

      return this.mapFirestoreToCoupon(querySnapshot.docs[0]);
    } catch (error) {
      console.error('Error getting coupon by code:', error);
      throw error;
    }
  }

  // Get all coupons with optional filters
  async getCoupons(filters?: CouponFilters): Promise<Coupon[]> {
    try {
      let q = query(collection(db, this.couponsCollection));

      // Apply filters
      if (filters?.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }

      if (filters?.createdBy) {
        q = query(q, where('createdBy', '==', filters.createdBy));
      }

      if (filters?.dateRange) {
        q = query(
          q,
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);
      const coupons = querySnapshot.docs.map((doc: any) => this.mapFirestoreToCoupon(doc));

      // Apply client-side filters
      let filteredCoupons = coupons;

      if (filters?.status && filters.status.length > 0) {
        filteredCoupons = filteredCoupons.filter((coupon: Coupon) => 
          filters.status!.includes(getCouponStatus(coupon))
        );
      }

      if (filters?.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredCoupons = filteredCoupons.filter((coupon: Coupon) =>
          coupon.code.toLowerCase().includes(searchTerm) ||
          coupon.name.toLowerCase().includes(searchTerm) ||
          (coupon.description && coupon.description.toLowerCase().includes(searchTerm))
        );
      }

      return filteredCoupons;
    } catch (error) {
      console.error('Error getting coupons:', error);
      throw error;
    }
  }

  // Validate a coupon code
  async validateCoupon(code: string, orderTotal: number): Promise<CouponValidationResult> {
    try {
      const coupon = await this.getCouponByCode(code);
      
      if (!coupon) {
        return {
          isValid: false,
          error: 'Coupon not found'
        };
      }

      if (!isCouponValid(coupon, orderTotal)) {
        const status = getCouponStatus(coupon);
        let error = 'Coupon is not valid';
        
        if (status === 'inactive') {
          error = 'Coupon is inactive';
        } else if (status === 'expired') {
          error = 'Coupon has expired';
        } else if (status === 'used_up') {
          error = 'Coupon usage limit reached';
        } else if (coupon.minimumOrderAmount && orderTotal < coupon.minimumOrderAmount) {
          error = `Minimum order amount of €${(coupon.minimumOrderAmount / 100).toFixed(2)} required`;
        }

        return {
          isValid: false,
          error
        };
      }

      const discountAmount = calculateCouponDiscount(coupon, orderTotal);
      
      return {
        isValid: true,
        coupon,
        discountAmount
      };
    } catch (error) {
      console.error('Error validating coupon:', error);
      return {
        isValid: false,
        error: 'Failed to validate coupon'
      };
    }
  }

  // Apply a coupon to an order
  async applyCoupon(couponId: string, orderId: string, customerEmail: string): Promise<CouponUsage> {
    try {
      const coupon = await this.getCoupon(couponId);
      if (!coupon) {
        throw new Error('Coupon not found');
      }

      // Create usage record
      const usageId = createCouponId();
      const usage: Omit<CouponUsage, 'id'> = {
        couponId,
        orderId,
        customerEmail,
        discountAmount: 0, // Will be calculated when order is processed
        usedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.couponUsageCollection), {
        ...usage,
        usedAt: Timestamp.fromDate(usage.usedAt)
      });

      // Increment coupon usage count
      await updateDoc(doc(db, this.couponsCollection, couponId), {
        usedCount: coupon.usedCount + 1,
        updatedAt: Timestamp.fromDate(new Date())
      });

      return {
        id: docRef.id,
        ...usage
      };
    } catch (error) {
      console.error('Error applying coupon:', error);
      throw error;
    }
  }

  // Get coupon usage history
  async getCouponUsage(couponId: string): Promise<CouponUsage[]> {
    try {
      const q = query(
        collection(db, this.couponUsageCollection),
        where('couponId', '==', couponId),
        orderBy('usedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc: any) => ({
        id: doc.id,
        couponId: doc.data().couponId,
        orderId: doc.data().orderId,
        customerEmail: doc.data().customerEmail,
        discountAmount: doc.data().discountAmount,
        usedAt: doc.data().usedAt.toDate()
      }));
    } catch (error) {
      console.error('Error getting coupon usage:', error);
      throw error;
    }
  }

  // Get coupon settings
  async getCouponSettings(): Promise<CouponSettings | null> {
    try {
      const settingsRef = doc(db, this.couponSettingsCollection, 'default');
      const settingsDoc = await getDoc(settingsRef);
      
      if (!settingsDoc.exists()) {
        return null;
      }

      return this.mapFirestoreToCouponSettings(settingsDoc);
    } catch (error) {
      console.error('Error getting coupon settings:', error);
      throw error;
    }
  }

  // Update coupon settings
  async updateCouponSettings(settings: Partial<CouponSettings>): Promise<CouponSettings> {
    try {
      const settingsRef = doc(db, this.couponSettingsCollection, 'default');
      const settingsDoc = await getDoc(settingsRef);
      
      if (!settingsDoc.exists()) {
        // Create default settings if they don't exist
        const defaultSettings = createDefaultCouponSettings();
        const newSettings = {
          ...defaultSettings,
          ...settings, // Apply the new settings
          createdAt: Timestamp.fromDate(defaultSettings.createdAt),
          updatedAt: Timestamp.fromDate(new Date())
        };
        
        await setDoc(settingsRef, newSettings);
        return this.mapFirestoreToCouponSettings({ id: 'default', data: () => newSettings });
      }

      const updateData = {
        ...settings,
        updatedAt: Timestamp.fromDate(new Date())
      };

      await updateDoc(settingsRef, updateData);

      // Return updated settings
      const updatedDoc = await getDoc(settingsRef);
      return this.mapFirestoreToCouponSettings(updatedDoc);
    } catch (error) {
      console.error('Error updating coupon settings:', error);
      throw error;
    }
  }

  // Get coupon statistics
  async getCouponStats(): Promise<CouponStats> {
    try {
      const coupons = await this.getCoupons();
      const activeCoupons = coupons.filter(coupon => coupon.isActive);
      const expiredCoupons = coupons.filter(coupon => {
        const status = getCouponStatus(coupon);
        return status === 'expired';
      });

      // Get total usage and discount given
      const usageQuery = query(collection(db, this.couponUsageCollection));
      const usageSnapshot = await getDocs(usageQuery);
      
      const totalUsage = usageSnapshot.size;
      const totalDiscountGiven = usageSnapshot.docs.reduce((sum: number, doc: any) => {
        return sum + (doc.data().discountAmount || 0);
      }, 0);

      // Find most used coupon
      const usageByCoupon = new Map<string, number>();
      usageSnapshot.docs.forEach((doc: any) => {
        const couponId = doc.data().couponId;
        usageByCoupon.set(couponId, (usageByCoupon.get(couponId) || 0) + 1);
      });

      let mostUsedCoupon: { coupon: Coupon; usageCount: number } | undefined = undefined;
      let maxUsage = 0;
      for (const [couponId, usageCount] of Array.from(usageByCoupon.entries())) {
        if (usageCount > maxUsage) {
          maxUsage = usageCount;
          const coupon = coupons.find(c => c.id === couponId);
          if (coupon) {
            mostUsedCoupon = { coupon, usageCount };
          }
        }
      }

      // Get recent usage (last 10)
      const recentUsageQuery = query(
        collection(db, this.couponUsageCollection),
        orderBy('usedAt', 'desc'),
        limit(10)
      );
      const recentUsageSnapshot = await getDocs(recentUsageQuery);
      const recentUsage = recentUsageSnapshot.docs.map((doc: any) => ({
        id: doc.id,
        couponId: doc.data().couponId,
        orderId: doc.data().orderId,
        customerEmail: doc.data().customerEmail,
        discountAmount: doc.data().discountAmount,
        usedAt: doc.data().usedAt.toDate()
      }));

      return {
        totalCoupons: coupons.length,
        activeCoupons: activeCoupons.length,
        expiredCoupons: expiredCoupons.length,
        totalUsage,
        totalDiscountGiven,
        mostUsedCoupon,
        recentUsage
      };
    } catch (error) {
      console.error('Error getting coupon stats:', error);
      throw error;
    }
  }

  // Helper method to map Firestore document to Coupon
  private mapFirestoreToCoupon(doc: any): Coupon {
    const data = doc.data();
    return {
      id: doc.id,
      code: data.code,
      name: data.name,
      description: data.description,
      discountType: data.discountType,
      discountValue: data.discountValue,
      minimumOrderAmount: data.minimumOrderAmount,
      maximumDiscountAmount: data.maximumDiscountAmount,
      usageLimit: data.usageLimit,
      usedCount: data.usedCount || 0,
      isActive: data.isActive,
      validFrom: data.validFrom.toDate(),
      validUntil: data.validUntil ? data.validUntil.toDate() : undefined,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      createdBy: data.createdBy
    };
  }

  // Helper method to map Firestore document to CouponSettings
  private mapFirestoreToCouponSettings(doc: any): CouponSettings {
    const data = doc.data();
    return {
      id: doc.id,
      isEnabled: data.isEnabled,
      allowMultipleCoupons: data.allowMultipleCoupons,
      maxCouponsPerOrder: data.maxCouponsPerOrder,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate()
    };
  }
}

export const couponService = new CouponService();
